import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export type Slide = { id: string; image: string; title_ar: string; title_en: string; body_ar: string; body_en: string };
export type OnboardingCfg = { enabled: boolean; slides: Slide[]; version: string };
const KEY = "wn_onboarding_seen";

export const OnboardingSlides = ({ slides, onClose }: { slides: Slide[]; onClose: () => void }) => {
  const { t, lang } = usePrefs();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const touch = useRef<number | null>(null);
  const last = i === slides.length - 1;
  const go = (n: number) => setI(Math.max(0, Math.min(slides.length - 1, n)));
  const rtl = lang === "ar";
  const onEnd = (x: number) => {
    if (touch.current == null) return;
    const dx = x - touch.current;
    touch.current = null;
    if (Math.abs(dx) < 40) return;
    go(i + ((dx < 0) !== rtl ? 1 : -1));
  };
  const s = slides[i];
  const pick = (ar: string, en: string) => (lang === "ar" ? ar : en || ar);

  return (
    <div data-testid="onboarding" role="dialog" aria-modal="true" className="fixed inset-0 z-[80] bg-black text-white"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={(e) => onEnd(e.changedTouches[0].clientX)}>
      {s.image && <img src={s.image} alt="" aria-hidden className="hidden md:block absolute inset-0 h-full w-full object-cover blur-2xl scale-110 opacity-40" />}
      <div className="relative h-full w-full md:max-w-md md:mx-auto md:shadow-2xl overflow-hidden flex flex-col">
      {slides.map((x, k) => (
        <div key={x.id} aria-hidden={k !== i} className={`absolute inset-0 transition-opacity duration-500 ${k === i ? "opacity-100" : "opacity-0"}`}>
          {x.image ? <img src={x.image} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-primary" />}
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
      <div className="relative flex justify-between items-center p-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <img src="/logo.png" alt="" className="h-10 w-10 rounded-xl" />
        {!last && <button onClick={onClose} data-testid="onboarding-skip" className="h-9 px-4 rounded-full bg-white/15 backdrop-blur text-sm font-bold">{t("onb.skip")}</button>}
      </div>
      <div className="relative mt-auto mx-auto w-full max-w-md px-6 pb-[max(2rem,env(safe-area-inset-bottom))] space-y-5">
        <div key={s.id} className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h2 data-testid="onboarding-title" className="font-display text-3xl font-black leading-tight">{pick(s.title_ar, s.title_en)}</h2>
          <p data-testid="onboarding-body" className="text-base text-white/85 leading-relaxed">{pick(s.body_ar, s.body_en)}</p>
        </div>
        <div className="flex gap-1.5" data-testid="onboarding-dots">
          {slides.map((x, k) => <button key={x.id} onClick={() => go(k)} aria-label={`${k + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] ${k === i ? "w-7 bg-primary" : "w-3 bg-white/40"}`} />)}
        </div>
        {last ? (
          <div className="grid gap-2">
            <button onClick={() => { onClose(); nav("/login"); }} data-testid="onboarding-register" className="h-13 py-3.5 rounded-full bg-primary text-primary-foreground font-bold text-base">{t("onb.register")}</button>
            <button onClick={onClose} data-testid="onboarding-browse" className="h-12 rounded-full bg-white/15 backdrop-blur font-bold">{t("onb.browse")}</button>
          </div>
        ) : (
          <div className="flex gap-2">
            {i > 0 && <button onClick={() => go(i - 1)} aria-label={t("onb.prev")} data-testid="onboarding-prev" className="h-12 w-12 grid place-items-center rounded-full bg-white/15 backdrop-blur">{rtl ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}</button>}
            <button onClick={() => go(i + 1)} data-testid="onboarding-next" className="flex-1 h-12 rounded-full bg-white text-black font-bold">{t("onb.next")}</button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export const Onboarding = () => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState<OnboardingCfg | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (user !== false) return;
    api.get("/onboarding").then((r) => {
      setCfg(r.data);
      if (r.data.enabled && r.data.slides.length && localStorage.getItem(KEY) !== r.data.version) setOpen(true);
    }).catch(() => {});
  }, [user]);
  if (!open || !cfg || user) return null;
  return <OnboardingSlides slides={cfg.slides} onClose={() => { localStorage.setItem(KEY, cfg.version); setOpen(false); }} />;
};
